import { FC, useCallback, useMemo } from 'react';
import { Alert, alpha, Box, Link, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ExtendedGridColDef } from '@/types/ExtendedGridColDef';
import { SCUIButton } from '../../../atomic/atoms';
import { SCUICustomDataGrid } from '../../../atomic/molecules';
import { PageUrl } from '../../../types/enums/pageUrl-enums';
import type { ShipmentRow } from '../autoReturnAndResend.types';
import { exportPanelToExcel } from '../autoReturnAndResend.helpers';
import { dataGridSx, renderLinkCell } from '../autoReturnAndResend.gridHelpers';
import { AutoReturnAndResendPanelHeader } from './AutoReturnAndResendPanelHeader';
import { ARAR } from '../autoReturnAndResend.classes';

interface AutoReturnAndResendAutoReturnPanelProps {
    rows: ShipmentRow[];
    onSendAutoReturn: (rows: ShipmentRow[]) => void;
    onBulkStartAutoReturn: () => void;
}

export const AutoReturnAndResendAutoReturnPanel: FC<AutoReturnAndResendAutoReturnPanelProps> = ({ rows, onSendAutoReturn, onBulkStartAutoReturn }) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const handleExportExcel = useCallback(() => {
        exportPanelToExcel(
            rows,
            [
                { key: 'orderNumber', label: t('general.orderNumber'), resolve: (row) => row.orderNumber ?? '-' },
                { key: 'shippingListNo', label: t('general.shippingNo'), resolve: (row) => row.shippingListNo },
            ],
            'OtomatikIade'
        );
    }, [rows, t]);

    const columns: ExtendedGridColDef[] = useMemo(
        () => [
            {
                field: 'orderNumber',
                headerName: t('general.orderNumber'),
                flex: 1,
                minWidth: 140,
                headerAlign: 'center',
                align: 'center',
                renderCell: (params) =>
                    renderLinkCell(`/orderdetail?OrderNumber=${params.row.orderNumber}`, params.row.orderNumber),
            },
            {
                field: 'shippingListNo',
                headerName: t('general.shippingNo'),
                flex: 1,
                minWidth: 140,
                headerAlign: 'center',
                align: 'center',
                renderCell: (params) =>
                    renderLinkCell(
                        `/orderdetail?shippingListNo=${params.row.shippingListNo}`,
                        params.row.shippingListNo
                    ),
            },
            {
                field: 'action',
                headerName: t('autoReturnAndResend.columns.action'),
                flex: 1,
                minWidth: 140,
                headerAlign: 'center',
                align: 'center',
                renderCell: (params) => (
                    <SCUIButton
                        text={t('autoReturnAndResend.actions.sendAutoReturn')}
                        type="secondary"
                        sizeType="fixed"
                        size="s"
                        style={{
                            backgroundColor: alpha(theme.palette.warning.main, 0.08),
                            color: theme.palette.warning.dark,
                            borderColor: theme.palette.warning.main,
                        }}
                        onClick={() => onSendAutoReturn([params.row])}
                    />
                ),
            },
        ],
        [onSendAutoReturn, t, theme]
    );

    return (
        <Box className={ARAR.panelSection}>
            <Alert severity="info" sx={{ mb: 2, alignItems: 'center' }}>
                <Typography component="span" sx={{ fontSize: '1.125rem', lineHeight: 1.6 }}>
                    {t('autoReturnAndResend.autoReturn.infoMessage')}{' '}
                    <Link
                        href={PageUrl.paymentConsolidation.url}
                        target="_blank"
                        rel="noreferrer"
                        sx={{ fontSize: 'inherit', fontWeight: 600 }}
                    >
                        {t('autoReturnAndResend.autoReturn.bulkShipmentSearchLink')}
                    </Link>
                </Typography>
            </Alert>
            <AutoReturnAndResendPanelHeader
                actions={
                    <>
                        <SCUIButton
                            text={t('autoReturnAndResend.actions.bulkAutoReturnStart')}
                            type="primary"
                            sizeType="default"
                            size="m"
                            disabled={rows.length === 0}
                            onClick={onBulkStartAutoReturn}
                        />
                        <SCUIButton
                            text={t('rpaResender.exportToExcel')}
                            type="secondary"
                            sizeType="default"
                            size="m"
                            disabled={rows.length === 0}
                            onClick={handleExportExcel}
                        />
                    </>
                }
            />
            <Box className={ARAR.panelGrid} sx={dataGridSx}>
                <SCUICustomDataGrid randomId rows={rows} columns={columns} pageSize={10} autoHeight />
            </Box>
        </Box>
    );
};
